import { AppDispatch, store } from "../store";
import { State as AuthState, getPatientGroup } from "../auth/authSlice";
import {
  BaseQueryApi,
  FetchArgs,
  FetchBaseQueryError,
  createApi,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";
import { LogEventData, LogoutReason } from "./logging";

import { googleLogout } from "@react-oauth/google";
import { clearMsalCache } from "../../msalService";
import { logOut } from "../../features/auth/authSlice";
import { setChatOpenUxState } from "../appState/uxSlice";

const isProd = () => {
  const host = window.location.hostname;
  return !host.includes(".staging.") && !host.includes("localhost");
};

const apiBaseURL = () => {
  const apiHost = isProd()
    ? "ficus.af-south-1.epistore.auderenow.io"
    : "ficus.af-south-1.epistore.staging.auderenow.io";
  return `https://${apiHost}/v1/ficus`;
};

const nonFatalEndpoints = [
  "logEvent",
  "setClinicianWorkingState",
  "pollHCWMessages",
  "sendHCWMessage",
  "sendHCWTemplate",
];

const baseQuery = fetchBaseQuery({
  baseUrl: apiBaseURL(),
  mode: "cors",
  credentials: "include",
  prepareHeaders: (headers, { getState }) => {
    headers.set("Content-Type", "application/json");
    if (!headers.get("X-Audere-API-Token")) {
      const state = getState() as { auth: AuthState };
      const token = state.auth.token;
      if (token) {
        headers.set("X-Audere-API-Token", `${token}`);
      }
    }
    return headers;
  },
  timeout: 45000,
});

export const logOutHelper = <T extends { util: { resetApiState: () => any } }>(
  dispatch: AppDispatch,
  apiSlice: T,
  logoutReason: LogoutReason,
  extraData?: Record<string, unknown>
) => {
  logEvent(dispatch, {
    event: "userLogout",
    data: { reason: logoutReason, ...extraData },
  } as LogEventData);
  const { authority } = (store.getState() as { auth: AuthState }).auth;
  if (!authority || authority === "accounts.google.com") {
    googleLogout();
  } else if (authority === "login.microsoftonline.com") {
    clearMsalCache();
  }
  dispatch(logOut());
  dispatch(apiSlice.util.resetApiState());
  dispatch(setChatOpenUxState(false));
};

const baseQueryWithReauth = async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: object
) => {
  if (api.endpoint !== "openIdAuthenticate" && api.endpoint !== "logEvent") {
    const getStateT = api.getState as () => { auth: AuthState };
    const state = getStateT();
    const auth = state.auth;
    if (
      auth.patientGroup?.[auth.email] &&
      !auth.patientGroups?.includes(auth.patientGroup[auth.email])
    ) {
      const refreshResult = {
        error: {
          status: "CUSTOM_ERROR",
          error: "Unauthorized role",
        } as FetchBaseQueryError,
      };
      logOutHelper(api.dispatch, apiSlice, LogoutReason.UnauthorizedRole);
      return refreshResult;
    }

    if (new Date(auth.expires) < new Date(Date.now())) {
      // Token expiry; log out. Possible TODO is to refresh the token.
      console.log("Expired token - logging out");
      const refreshResult = {
        error: {
          status: "CUSTOM_ERROR",
          error: "Token has expired",
        } as FetchBaseQueryError,
      };
      logOutHelper(api.dispatch, apiSlice, LogoutReason.TokenExpired);
      return refreshResult;
    }
  }

  // Enable this for testing error handling in sendHCWMessage and sendHCWTemplate
  /*
  if (api.endpoint === "sendHCWMessage" || api.endpoint === "sendHCWTemplate") {
    console.log(`Faking error for ${api.endpoint}`);
    await new Promise((resolve) =>
      setTimeout(resolve, Math.random() * 3000 + 2000)
    );
    const sendMessageResult = {
      error: {
        status: 500,
        data: {
          message: "This error is faked for testing purposes",
        },
      } as FetchBaseQueryError,
    };
    return sendMessageResult;
  }
  */

  const result = await baseQuery(args, api, extraOptions);
  if (
    result?.error?.status === "FETCH_ERROR" &&
    !nonFatalEndpoints.includes(api.endpoint)
  ) {
    console.log(`Fetch error from ${api.endpoint} - logging out`);
    logOutHelper(api.dispatch, apiSlice, LogoutReason.ApiError, {
      method_id: api.endpoint,
      error: result.error.error,
    });
  }

  return result;
};

export enum DataRoles {
  reader = "reader",
  writer = "writer",
}

type Roles = {
  roles: DataRoles[];
};

export interface GetRolesData {
  patient_groups: {
    [group: string]: Roles & {
      subgroups?: {
        [subgroup: string]: Roles;
      };
      whatsapp_voice_call_enabled?: boolean;
    };
  };
}

export const apiSlice = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    logEvent: builder.mutation<string, LogEventData>({
      query: (args) => {
        const logId = import.meta.env.VITE_LOG_ID;
        if (!logId) {
          throw new Error(
            "VITE_LOG_ID is not set so cannot log events. If running locally, add a .env file with this variable included."
          );
        }
        return {
          url: "/portal_log",
          method: "POST",
          body: JSON.stringify({
            timestamp: new Date().toISOString(),
            ...args,
          }),
          headers: [["X-Api-Key", `${logId}`]],
        };
      },
      transformResponse: (rawResult: {
        uid: string;
        response_code: string;
      }) => {
        console.log("Raw result from portal_log");
        console.log(rawResult);
        return rawResult.uid;
      },
    }),
    getRoles: builder.query<GetRolesData, string>({
      query: (token) => ({
        url: "/portal_api",
        method: "POST",
        body: JSON.stringify({
          method_id: "get_roles",
          input: {},
        }),
        headers: [["X-Audere-API-Token", `${token}`]],
      }),
      transformResponse: (rawResult: PortalApiResult<GetRolesData>) => {
        console.log("Raw result from get_roles");
        console.log(rawResult);
        return rawResult.output;
      },
    }),
  }),
});

export const logEvent = (dispatch: AppDispatch, event: LogEventData) => {
  event.group = getPatientGroup(store.getState().auth);
  dispatch(apiSlice.endpoints.logEvent.initiate(event));
};

export interface PortalApiResult<T> {
  request_uid: string;
  method_id: string;
  response_code: string;
  output: T;
}

export const { useLazyGetRolesQuery } = apiSlice;
